import { ActivitySquareIcon } from "lucide-react";

interface RelayEntry {
  stadiumName: string;
  relayName: string;
  alive: boolean;
  lastSeen: string | null; // ISO string or null
  relayType?: string;       // "tracab" | "hawkeye" | undefined (undefined = championdata behaviour)
  // tracab/hawkeye-specific (ignored for other types)
  status?: string;          // "idle" | "streaming" | "offline"
  nextMatch?: string;
  hoursUntilKo?: number;
}

interface RelayIndicatorProps {
  entries: RelayEntry[];
  wsConnected?: boolean;
}

export default function RelayIndicator({ entries, wsConnected = true }: RelayIndicatorProps) {
  if (!wsConnected || entries.length === 0) return null;

  const timeoutMs = 90_000;

  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 text-sm">
      {entries.map((entry) => {
        const seen = entry.lastSeen ? new Date(entry.lastSeen).getTime() : null;
        const localAlive = !!seen && (Date.now() - seen) < timeoutMs;
        const alive = entry.alive !== undefined ? entry.alive : localAlive;

        // ── Tracab / Hawkeye relay ─────────────────────────────────────
        if (entry.relayType === "tracab" || entry.relayType === "hawkeye") {
          const isOffline   = !alive || entry.status === "offline";
          const isStreaming  = !isOffline && entry.status === "streaming";
          const isIdle       = !isOffline && !isStreaming;

          const statusColor = isStreaming ? "text-green-600"
                            : isIdle     ? "text-yellow-500"
                            :              "text-red-600";
          const statusLabel = isStreaming ? "LIVE"
                            : isIdle     ? "IDLE"
                            :              "offline";

          return (
            <div key={`${entry.stadiumName}-${entry.relayName}`} className="bg-white shadow-sm rounded-xl border border-blue-100 px-3 py-2 flex flex-col">
              <div className="flex items-center gap-1.5">
                <ActivitySquareIcon className="w-4 h-4 text-gray-600" />
                <span>{entry.stadiumName} &mdash; {entry.relayName}</span>
                <span className={statusColor}>{statusLabel}</span>
              </div>
              {isIdle && entry.nextMatch && (
                <div className="text-xs text-gray-500 mt-0.5 ml-5">
                  {entry.nextMatch}
                  {entry.hoursUntilKo != null && ` · ${entry.hoursUntilKo}h to KO`}
                </div>
              )}
            </div>
          );
        }

        // ── Default / championdata relay (Marvel) — unchanged ───────────
        const lastSeenStr = seen ? new Date(seen).toLocaleTimeString() : "never";
        return (
          <div key={`${entry.stadiumName}-${entry.relayName}`} className="bg-white shadow-sm rounded-xl border border-blue-100 px-3 py-2 flex items-center gap-1.5">
            <ActivitySquareIcon className="w-4 h-4 text-gray-600" />
            <span>{entry.stadiumName} &mdash; {entry.relayName}</span>
            <span className={alive ? "text-green-600" : "text-red-600"}>
              {alive ? "online" : "offline"}
            </span>
            <span className="text-gray-500">· last {lastSeenStr} UTC</span>
          </div>
        );
      })}
    </div>
  );
}
